import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Button,
  Spin,
  Divider,
  theme,
  Space,
  Typography,
} from "antd";
import { LoadingOutlined, CheckCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import UserService, {
  roleOption,
  supportRoleOption,
} from "../../Services/UserService";
import { useUser } from "../../Context/UserContext";
import { adminContext } from "../../Common/PageRoute";
import { Roles } from "../../Services/AuthService";
import { EditUserHeader } from "./Components/EditUserHeader";
import { UserProfileFormSection } from "./Components/UserProfileFormSection";
import { UserRoleFormSection } from "./Components/UserRoleFormSection";

const { Text } = Typography;

interface EditUserPanelProps {
  open: boolean;
  userId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditUserPanel: React.FC<EditUserPanelProps> = ({
  open,
  userId,
  onClose,
  onSuccess,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const { isSuperAdmin, isSubAdmin, isSupportAdmin } = useUser();
  const { openNotification } = React.useContext(adminContext);

  const canChangeRole = isSuperAdmin() || isSubAdmin() || isSupportAdmin();
  const roleOptionsList = isSuperAdmin() ? roleOption : supportRoleOption;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(
    null,
  );

  // Fetch complete user details when drawer opens
  useEffect(() => {
    if (open && userId) {
      setLoading(true);
      setGeneratedPassword(null);
      UserService.getUserDetails(userId)
        .then((res: any) => {
          if (res?.status && res?.result) {
            const u = res.result;
            setUserData(u);

            // Determine matching role key
            const rList: string[] = u?.roles ?? (u?.role ? [u.role] : []);
            const foundRole = rList.find((x: string) => {
              const upper = (x || "").toUpperCase();
              return [
                "SUPERADMIN",
                "SUPERADMINC",
                "SUBADMIN",
                "SUBADMINC",
                "SUPPORTADMIN",
                "SUPPORTMANAGER",
                "SUPPORTUSER",
              ].includes(upper);
            });

            let mappedRole = u?.role;
            if (foundRole) {
              const upperFound = foundRole.toUpperCase();
              if (upperFound === "SUPERADMIN" || upperFound === "SUPERADMINC")
                mappedRole = Roles.SuperAdminC;
              else if (upperFound === "SUBADMIN" || upperFound === "SUBADMINC")
                mappedRole = Roles.SubAdminC;
              else if (upperFound === "SUPPORTADMIN")
                mappedRole = Roles.SupportAdminC;
              else if (upperFound === "SUPPORTMANAGER")
                mappedRole = Roles.SupportManagerC;
              else if (upperFound === "SUPPORTUSER")
                mappedRole = Roles.SupportUserC;
            }

            form.setFieldsValue({
              id: u.id,
              name: {
                title: u.name?.title || u.name?.Title,
                first: u.name?.first || u.name?.First,
                last: u.name?.last || u.name?.Last,
              },
              email: u.email,
              phoneNumber: u.phoneNumber,
              gender: u.gender && u.gender > 0 ? u.gender : undefined,
              birthDate: u.birthDate
                ? dayjs(u.birthDate, "DD/MM/YYYY")
                : undefined,
              role: mappedRole,
            });
          } else {
            openNotification(
              "error",
              res?.message || "Failed to load user details.",
            );
          }
        })
        .catch(() => {
          openNotification("error", "Error connecting to service.");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      form.resetFields();
      setUserData(null);
    }
  }, [open, userId]);

  const handleSave = async (values: any) => {
    if (!userId) return;
    setSaving(true);
    try {
      const payload = {
        ...values,
        birthDateStr: values.birthDate
          ? dayjs(values.birthDate).format("DD/MM/YYYY")
          : undefined,
        userName: values.email,
        practiceId: userData?.practiceId || 0,
      };

      const res = await UserService.updateUserDetails(userId, payload);
      if (res?.status) {
        openNotification(
          "success",
          `User "${values.name?.first || ""} ${values.name?.last || ""}" updated successfully.`,
        );
        onSuccess();
        onClose();
      } else {
        openNotification(
          "error",
          res?.message || "Failed to update user details.",
        );
      }
    } catch (err: any) {
      openNotification(
        "error",
        err?.message || "An error occurred while saving.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userId) return;
    setResettingPassword(true);
    try {
      const res = await UserService.resetPassword(userId);
      if (res?.status && res?.result) {
        setGeneratedPassword(res.result);
        openNotification("success", "Password reset successfully!");
      } else {
        openNotification("error", res?.message || "Failed to reset password.");
      }
    } catch {
      openNotification("error", "Error resetting password.");
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <Drawer
      title={null}
      placement="right"
      width={560}
      onClose={onClose}
      open={open}
      destroyOnClose
      closeIcon={null}
      styles={{
        header: { display: "none" },
        body: {
          padding: 0,
          overflowX: "hidden",
          background: token.colorBgContainer,
        },
        footer: {
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          padding: "12px 24px",
          textAlign: "right",
          background: token.colorBgContainer,
        },
      }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Button onClick={onClose} disabled={saving || loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={saving || loading}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <Spin
        spinning={loading}
        indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />}
      >
        {/* Header Component */}
        <EditUserHeader userData={userData} onClose={onClose} />

        {/* Generated Password Notice Banner */}
        {generatedPassword && (
          <div
            style={{
              padding: "12px 24px",
              background: token.colorSuccessBg,
              borderBottom: `1px solid ${token.colorSuccessBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Space align="center" size={10}>
              <CheckCircleOutlined
                style={{ color: token.colorSuccess, fontSize: 16 }}
              />
              <div>
                <Text
                  style={{
                    fontSize: 12,
                    color: token.colorText,
                    display: "block",
                  }}
                >
                  New password generated:
                </Text>
                <Text
                  code
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: token.colorSuccess,
                  }}
                >
                  {generatedPassword}
                </Text>
              </div>
            </Space>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                navigator.clipboard.writeText(generatedPassword);
                openNotification("success", "Password copied to clipboard!");
              }}
            >
              Copy
            </Button>
          </div>
        )}

        {/* Form Container */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
          style={{ padding: "20px 24px 24px" }}
        >
          {/* Section 1: User Profile Details Sub-component */}
          <UserProfileFormSection />

          <Divider
            style={{
              margin: "16px 0 20px",
              borderColor: token.colorBorderSecondary,
            }}
          />

          {/* Section 2: Role & Security Sub-component */}
          <UserRoleFormSection
            canChangeRole={canChangeRole}
            roleOptionsList={roleOptionsList}
            resettingPassword={resettingPassword}
            onResetPassword={handleResetPassword}
          />
        </Form>
      </Spin>
    </Drawer>
  );
};
